import { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import ReactDOM from "react-dom";

import Button from "components/Button";
import styles from "./style.module.scss";

interface IProps {
  isOpen?: boolean;
  closeModal: VoidFunction;
  children?: React.ReactNode;
  modalContainerClass?: string;
  dialogClass?: string;
  closeClass?: string;
  wrapperClass?: string;
  isDisableCloseModal?: boolean;
  showCloseButton?: boolean;
  zIndex?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
}

function extractZIndex(zIndexStr: string): number {
  // Check for custom z-index pattern z-[123]
  const customMatch = zIndexStr.match(/z-\[(\d+)\]/);
  if (customMatch) {
    return parseInt(customMatch[1], 10);
  }
  // Check for standard z-index pattern z-123
  const standardMatch = zIndexStr.match(/z-(\d+)/);
  if (standardMatch) {
    return parseInt(standardMatch[1], 10);
  }

  return 80; // default z-index
}

export default function Modal({
  isOpen = false,
  closeModal,
  children,
  modalContainerClass,
  dialogClass,
  closeClass,
  wrapperClass,
  isDisableCloseModal = false,
  showCloseButton = false,
  zIndex = "z-[108]",
  size = "md"
}: Readonly<IProps>) {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  const baseZIndex = extractZIndex(zIndex);
  const overlayZIndex = baseZIndex;
  const modalZIndex = baseZIndex + 1;

  // Determine the size class based on the size prop
  const getSizeClass = () => {
    switch (size) {
      case "xs":
        return styles.smartModalDialogXs;
      case "sm":
        return styles.smartModalDialogSm;
      case "md":
        return styles.smartModalDialogMd;
      case "lg":
        return styles.smartModalDialogLg;
      case "xl":
        return styles.smartModalDialogXl;
      case "xxl":
        return styles.smartModalDialogXxl;
      default:
        return styles.smartModalDialogMd;
    }
  };

  const sizeClass = getSizeClass();

  const modalContent = (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-y-auto"
        onClose={isDisableCloseModal ? () => null : closeModal}
        style={{ zIndex: overlayZIndex }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div
            className={clsx(
              "fixed inset-0",
              isDisableCloseModal ? "bg-card/60 backdrop-blur-md" : "bg-card/90"
            )}
            style={{ zIndex: overlayZIndex, pointerEvents: "auto" }}
          />
        </Transition.Child>

        <div
          className={clsx(
            "fixed inset-0 overflow-y-auto",
            styles.smartModalContainer,
            modalContainerClass
          )}
          style={{ zIndex: modalZIndex }}>
          <div className={clsx(styles.smartModalWrapper, wrapperClass)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Dialog.Panel className={clsx(styles.smartModalDialog, sizeClass, dialogClass)}>
                <div className="flex items-center justify-between">
                  {showCloseButton && !isDisableCloseModal && (
                    <Button
                      className={clsx(styles.smartModalClose, closeClass)}
                      rounded
                      iconButton
                      onClick={closeModal}
                      aria-label="Close Modal">
                      <Icon path={mdiClose} className="w-5 h-5" />
                    </Button>
                  )}
                </div>
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
